/**
 * Canonical stats tick extracted from a PeerConnection. Produced by
 * `getStatsSnapshot` and consumed by `halfFromStats` (for the wire format)
 * and by `computeQuality` (for signal bar derivation).
 */
export interface WebRTCStats {
  rtt: number          // round-trip time in ms
  jitter: number       // inbound jitter in ms
  packetsLost: number
  packetsReceived: number
  codec: string | null     // e.g. "opus" — extracted from inbound-rtp mimeType
  bytesReceived: number    // cumulative inbound bytes — used for end bitrate
}

export const ZERO_WEBRTC_STATS: WebRTCStats = {
  rtt: 0, jitter: 0, packetsLost: 0, packetsReceived: 0,
  codec: null, bytesReceived: 0,
}

/**
 * Pure stats extraction from a PeerConnection. Caller decides when to
 * poll (e.g. once per second while connectionState === 'connected').
 * Keeping this pure makes it unit-testable without a real RTCPeerConnection.
 *
 * Field sources:
 * - `rtt`   — nominated candidate-pair's `currentRoundTripTime` (seconds → ms)
 * - `jitter`/`packetsLost`/`packetsReceived`/`bytesReceived` — audio inbound-rtp
 * - `codec` — inbound-rtp's `codecId` → report.get() → mimeType → suffix
 *
 * Non-audio inbound-rtp (e.g. video) is ignored. Returns zeros when the
 * report has nothing interesting in it.
 */
export async function getStatsSnapshot(pc: RTCPeerConnection): Promise<WebRTCStats> {
  const report = await pc.getStats()
  let rtt = 0
  let jitter = 0
  let packetsLost = 0
  let packetsReceived = 0
  let bytesReceived = 0
  let codecId: string | undefined
  report.forEach(s => {
    if (s.type === 'candidate-pair' && s.nominated) {
      rtt = Math.round((s.currentRoundTripTime ?? 0) * 1000)
    }
    if (s.type === 'inbound-rtp' && s.kind === 'audio') {
      jitter = Math.round((s.jitter ?? 0) * 1000)
      packetsLost = s.packetsLost ?? 0
      packetsReceived = s.packetsReceived ?? 0
      bytesReceived = s.bytesReceived ?? 0
      codecId = s.codecId
    }
  })
  let codec: string | null = null
  if (codecId) {
    const codecStat = report.get(codecId) as { mimeType?: string } | undefined
    if (codecStat?.mimeType) {
      codec = codecStat.mimeType.split('/').pop() ?? null
    }
  }
  return { rtt, jitter, packetsLost, packetsReceived, codec, bytesReceived }
}

/**
 * Half of a call-quality report snapshot. Mirrors the backend
 * `CallQualitySnapshot` Pydantic model. All fields are optional on the
 * wire — backend treats missing fields as null.
 */
export interface CallQualityHalf {
  rtt_ms: number
  jitter_ms: number
  packets_lost: number
  packets_received: number
  codec: string | null
  /** End-of-call only; start snapshots have no delta to compute bitrate from. */
  bitrate_kbps?: number | null
}

/**
 * v2 per-tick aggregates. Mirrors backend `CallQualityAggregates` Pydantic
 * model and the `TickAggregates` interface from tickBuffer.ts. All values
 * computed over voice-only ticks (DTX excluded), unless field name says
 * otherwise.
 *
 * Legacy clients (older bundles) won't send this field; backend treats it
 * as None and leaves the v2 columns NULL.
 */
export interface CallQualityAggregates {
  rtt_p50_ms: number
  rtt_p95_ms: number
  jitter_p95_ms: number
  loss_ratio_avg: number
  loss_high_time_ratio: number
  dtx_ratio: number
  pps_avg: number
  bitrate_avg_kbps: number
  bitrate_p95_kbps: number
  legacy_quality_v1: number  // 1..4 — what the old formula said for this call
  aggregator_version: number
}

export interface CallQualityCombined {
  start: CallQualityHalf | null
  end: CallQualityHalf | null
  aggregates?: CallQualityAggregates | null
}

/** Extract a half snapshot from a WebRTCStats tick. No bitrate here. */
export function halfFromStats(s: WebRTCStats): CallQualityHalf {
  return {
    rtt_ms: s.rtt,
    jitter_ms: s.jitter,
    packets_lost: s.packetsLost,
    packets_received: s.packetsReceived,
    codec: s.codec,
  }
}

/**
 * Compute end bitrate kbps from bytesReceived delta.
 *
 * - `firstBytes` is the bytesReceived value at the first active tick.
 * - `lastBytes` is the bytesReceived at the end tick.
 * - `durationMs` is the elapsed time between the two ticks.
 *
 * Returns null if we never captured a first tick (call shorter than one
 * stats interval) — backend stores null and admin UI shows `?`.
 */
export function computeBitrateKbps(args: {
  firstBytes: number | null
  lastBytes: number
  durationMs: number
}): number | null {
  if (args.firstBytes === null) return null
  if (args.durationMs <= 0) return null
  const delta = Math.max(0, args.lastBytes - args.firstBytes)
  const kbps = (delta * 8) / args.durationMs // bytes × 8 bits / ms = kbps
  return Math.round(kbps)
}

/**
 * Fire-and-forget POST of a combined snapshot. Uses `keepalive: true` so
 * the browser delivers it even if the tab is closing right after hangup.
 *
 * Intentionally bypasses the shared `request()` wrapper — its 401 path
 * would try to redirect on failure, and we don't want stats delivery to
 * interfere with call teardown.
 */
export function sendCallStats(
  callId: string,
  payload: CallQualityCombined,
  apiBase: string,
): Promise<void> {
  if (!payload.start && !payload.end && !payload.aggregates) return Promise.resolve()
  return fetch(`${apiBase}/api/v1/calls/${encodeURIComponent(callId)}/stats`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  })
    .then(() => undefined)
    .catch((err) => {
      // Best-effort: a dropped stats POST must not break the call UI,
      // but do surface the failure so regressions aren't silent.
      console.warn('sendCallStats failed', err)
    })
}
